import React, { useEffect } from 'react'
import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { orderBy } from 'lodash-es'

//components
import PreferenceTagItem from './PreferenceTagItem'

// libs
import { TPreference } from '@/types/preference-tag'
import { usePreferences } from '@/hooks/usePreferences'
import { usePreferenceContext } from '@/components/pages/livemallclub/Explore'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import { isFlutterApp } from '@/utils/navigator'

// Styles
import styles from './style.module.scss'
const cx = classnames.bind(styles)

type TPreferenceMenuProps = {
  isOpen: boolean
  isEditMode: boolean
}

const sortPreferenceTags = (tags: TPreference[]) => {
  return orderBy(tags, [(item) => (item.isDefault ? 1 : 0), 'preferenceTag'], ['desc', 'asc'])
}

const PreferenceMenu = ({ isOpen, isEditMode }: TPreferenceMenuProps) => {
  const { allPreferencesTagList, memberPreferencesTagList, mutateMemberPreferenceTag, unSelectedPreferenceTagList } = usePreferences()
  const { editModeSelectedData, setEditModeSelectedData } = usePreferenceContext()
  const sortedEditModeSelectedData = sortPreferenceTags(editModeSelectedData)
  const sortedMemberPreferencesTagList = sortPreferenceTags(memberPreferencesTagList)
  const { t } = useTranslation('common')
  const router = useRouter()
  const currentPreferenceTag = router.query.preferenceTag
  const hiddenPreferenceTags = new Set([t('Preferences_History'), t('Preferences_Favorites'), t('Preferences_For_You'), t('Preferences_VIP')])

  useEffect(() => {
    setEditModeSelectedData(memberPreferencesTagList)
  }, [memberPreferencesTagList])

  const handleUnSelectedItemClick = (data: TPreference) => {
    const currentTags = (isEditMode ? editModeSelectedData : memberPreferencesTagList).map((item) => item.preferenceTag)
    mutateMemberPreferenceTag({ preferenceTagList: [...currentTags, data.preferenceTag] })
    if (isEditMode) {
      setEditModeSelectedData((prev) => [...prev, data])
    }
  }
  const handleMyPreferencesClick = (data: TPreference) => {
    if (data.isDefault) return
    if (isEditMode) {
      const newList = editModeSelectedData.filter((item) => item.preferenceTag !== data.preferenceTag)
      setEditModeSelectedData(newList)
      mutateMemberPreferenceTag({ preferenceTagList: newList.map((item) => item.preferenceTag) })
      if (data.preferenceTag === Number(currentPreferenceTag)) {
        handleRoutePushWithQuery({
          router,
          pathname: router.pathname,
          query: { preferenceTag: editModeSelectedData[0].preferenceTag },
        })
      }
    }
  }
  const myPreferencesTagDisplayList =
    (isEditMode ? sortedEditModeSelectedData : sortedMemberPreferencesTagList).filter((item) => !hiddenPreferenceTags.has(item.tagName)) ?? []
  const unSelectedListInEditMode = allPreferencesTagList.filter(
    (item) => !editModeSelectedData.some((selectedItem) => selectedItem.preferenceTag === item.preferenceTag),
  )
  const unSelectedPreferenceTagDisplayList = isEditMode ? unSelectedListInEditMode : unSelectedPreferenceTagList
  return (
    <div className={cx('menu', { isFlutterApp })} data-open={isOpen}>
      <div className={cx('menu-wrapper')}>
        <div className={cx('menu-content')}>
          <div className={cx('menu-content-title')}>{t('Common_My_Preferences')}</div>
          <div className={cx('menu-content-tags')}>
            {myPreferencesTagDisplayList.map((item) => (
              <PreferenceTagItem key={item.preferenceTag} {...item} isEditMode={isEditMode} handleItemClick={handleMyPreferencesClick} />
            ))}
          </div>
        </div>
        <div className={cx('menu-content')}>
          <div className={cx('menu-content-title')}>{t('Common_More_Preferences')}</div>
          <div className={cx('menu-content-tags')}>
            {unSelectedPreferenceTagDisplayList.map((item) => (
              <PreferenceTagItem
                key={item.preferenceTag}
                {...item}
                isEditMode={false}
                handleItemClick={handleUnSelectedItemClick}
                icon={
                  <svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M8.00002 2.66669L8.00002 13.3334M13.3334 8.00002L2.66669 8.00002'
                      stroke='#303030'
                      strokeWidth='1.8'
                      strokeLinecap='round'
                    />
                  </svg>
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PreferenceMenu
